/* iter461 — Nutrition Intelligence Lab.
 *
 * Premium food-discovery engine. UX inspired by InsideTracker / ZOE but
 * powered by Nutramilo proprietary indices (DIL / NIS / UIS / FMS).
 *
 * Flow (MVP — Phase 1-3):
 *   1. User picks Conditions / Phenotypes / Diets / Index priorities
 *   2. POST /api/foodlab/search → ranked foods
 *   3. Click food card → AI explanation via POST /api/foodlab/explain
 */
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { toast } from "sonner";
import MenuModalView from "@/components/foodlab/MenuModalView";  // iter466 — shared menu renderer
import NutritionLabDisclaimerModal, { hasFoodlabConsent } from "@/components/foodlab/NutritionLabDisclaimerModal";  // iter484 — first-visit educational disclaimer
import HowToStartCard from "@/components/HowToStartCard";  // iter503 — guided intro
import LoadFromVaultBanner from "@/components/LoadFromVaultBanner";  // iter906 — Pathway Vault as source of truth
import MyPolyphenolsCard from "@/components/MyPolyphenolsCard";  // iter907 — PhenolExplorer × CTD
import {
  FlaskConical, Search, Sparkles, ChevronDown, ChevronRight, X,
  TrendingUp, Loader2, Filter, Sliders, Salad, HeartPulse, Activity, BookOpen,
  Wand2, RotateCw, Plus, Star, Notebook, Utensils, Scale, Check,
  Dna, ChefHat, ExternalLink, Info, AlertTriangle,
} from "lucide-react";
import ProfileContextStrip from "@/components/shared/ProfileContextStrip";

const T = {
  bg: {
    title: "Nutrition Intelligence Lab",
    subtitle: "AI-движима хранителна интелигентност за твоя метаболитен профил.",
    cta: "Открий храните за моя профил",
    searching: "Анализирам {n} храни…",
    no_results: "Няма съвпадащи храни. Опитай с по-малко филтри.",
    section_conditions: "Здравословни състояния",
    section_phenotypes: "Метаболитни фенотипи",
    section_diets: "Диетични модели",
    section_indices: "Nutramilo приоритети (плъзгачи 0-100)",
    results_title: "Топ препоръки",
    results_total: "{n} храни анализирани",
    why_recommended: "Защо Nutramilo препоръчва?",
    components: { disease: "Заболявания", phenotype: "Фенотипи", diet: "Диета", indices: "Индекси", nutrient: "Нутриенти" },
    disclaimer: "Educational · Не е медицински съвет",
    recommend_title: "Препоръчани филтри",
    recommend_apply: "Приложи препоръките",
    recommend_dismiss: "Скрий",
    load_more: "Зареди още",
    regenerate: "Преоткрий нови храни",
    pool_label: "Анализирани храни",
    save_to_diary: "Запиши в дневник",
    add_to_plate: "Добави в чиния",
    pin_to_hi: "Закачи в Health Intelligence",
    unpin_from_hi: "Премахни от Health Intelligence",
    pick_meal_type: "Хранене",
    serving_g: "Порция (г)",
    confirm: "Запиши",
    compare_select: "Сравни",
    pick_for_menu: "Към меню",
    picked_count: "{n} храни избрани за меню",
    generate_from_picks: "Генерирай меню от избраните",
    picks_cleared: "Изборът е изчистен",
    clear_picks: "Изчисти избора",
    compare_cta: "Сравни {n} храни",
    compare_title: "Сравнение на храни",
    compare_winner: "Победител",
    breakfast: "Закуска",
    lunch: "Обяд",
    dinner: "Вечеря",
    snack: "Снакс",
    meal_added: "Добавено в дневника",
    pinned: "Закачено в HI Dashboard",
    unpinned: "Премахнато от HI Dashboard",
    kegg_title: "KEGG метаболитни пътища",
    kegg_subtitle: "Кои биологични пътища са засегнати от избраните състояния",
    kegg_show: "Покажи KEGG пътища",
    kegg_hide: "Скрий",
    kegg_select_conditions: "Избери поне 1 състояние, за да видиш KEGG пътищата.",
    kegg_view_on_kegg: "KEGG",
    menu_generate: "Генерирай AI меню за седмицата",
    menu_generating: "GPT-5.2 генерира менюто…",
    menu_title: "AI меню — GPT-5.2",
    menu_days_label: "Дни",
    menu_meals_label: "Хранения/ден",
    menu_no_conditions: "Избери поне 1 състояние, за да генерираш меню.",
  },
  en: {
    title: "Nutrition Intelligence Lab",
    subtitle: "AI-driven food intelligence tailored to your metabolic profile.",
    cta: "Discover foods for my profile",
    searching: "Analyzing {n} foods…",
    no_results: "No matching foods. Try fewer filters.",
    section_conditions: "Health conditions",
    section_phenotypes: "Metabolic phenotypes",
    section_diets: "Dietary patterns",
    section_indices: "Nutramilo priorities (sliders 0-100)",
    results_title: "Top recommendations",
    results_total: "{n} foods analyzed",
    why_recommended: "Why Nutramilo recommends?",
    components: { disease: "Conditions", phenotype: "Phenotypes", diet: "Diet", indices: "Indices", nutrient: "Nutrients" },
    disclaimer: "Educational · Not medical advice",
    recommend_title: "Recommended filters",
    recommend_apply: "Apply recommendations",
    recommend_dismiss: "Dismiss",
    load_more: "Load more",
    regenerate: "Discover new foods",
    pool_label: "Foods analyzed",
    save_to_diary: "Save to diary",
    add_to_plate: "Add to plate",
    pin_to_hi: "Pin to Health Intelligence",
    unpin_from_hi: "Unpin from Health Intelligence",
    pick_meal_type: "Meal",
    serving_g: "Serving (g)",
    confirm: "Save",
    compare_select: "Compare",
    pick_for_menu: "For menu",
    picked_count: "{n} foods picked for menu",
    generate_from_picks: "Generate menu from picks",
    picks_cleared: "Selection cleared",
    clear_picks: "Clear picks",
    compare_cta: "Compare {n} foods",
    compare_title: "Food comparison",
    compare_winner: "Winner",
    breakfast: "Breakfast",
    lunch: "Lunch",
    dinner: "Dinner",
    snack: "Snack",
    meal_added: "Added to diary",
    pinned: "Pinned to HI Dashboard",
    unpinned: "Unpinned from HI Dashboard",
    kegg_title: "KEGG metabolic pathways",
    kegg_subtitle: "Which biological pathways are affected by the selected conditions",
    kegg_show: "Show KEGG pathways",
    kegg_hide: "Hide",
    kegg_select_conditions: "Pick at least 1 condition to see KEGG pathways.",
    kegg_view_on_kegg: "KEGG",
    menu_generate: "Generate weekly AI menu",
    menu_generating: "GPT-5.2 is generating…",
    menu_title: "AI menu — GPT-5.2",
    menu_days_label: "Days",
    menu_meals_label: "Meals/day",
    menu_no_conditions: "Pick at least 1 condition to generate a menu.",
  },
  es: {
    title: "Laboratorio de Inteligencia Nutricional",
    subtitle: "Inteligencia alimentaria basada en AI adaptada a tu perfil metabólico.",
    cta: "Descubrir alimentos para mi perfil",
    searching: "Analizando {n} alimentos…",
    no_results: "No hay alimentos que coincidan. Prueba con menos filtros.",
    section_conditions: "Condiciones de salud",
    section_phenotypes: "Fenotipos metabólicos",
    section_diets: "Patrones dietéticos",
    section_indices: "Prioridades Nutramilo (sliders 0-100)",
    results_title: "Principales recomendaciones",
    results_total: "{n} alimentos analizados",
    why_recommended: "¿Por qué recomienda Nutramilo?",
    components: { disease: "Condiciones", phenotype: "Fenotipos", diet: "Dieta", indices: "Índices", nutrient: "Nutrientes" },
    disclaimer: "Educativo · No es consejo médico",
    recommend_title: "Filtros recomendados",
    recommend_apply: "Aplicar recomendaciones",
    recommend_dismiss: "Descartar",
    load_more: "Cargar más",
    regenerate: "Descubrir nuevos alimentos",
    pool_label: "Alimentos analizados",
    save_to_diary: "Guardar en el diario",
    add_to_plate: "Añadir al plato",
    pin_to_hi: "Fijar en Health Intelligence",
    unpin_from_hi: "Desfijar de Health Intelligence",
    pick_meal_type: "Comida",
    serving_g: "Ración (g)",
    confirm: "Guardar",
    compare_select: "Comparar",
    pick_for_menu: "Para el menú",
    picked_count: "{n} alimentos seleccionados para el menú",
    generate_from_picks: "Generar menú a partir de la selección",
    picks_cleared: "Selección borrada",
    clear_picks: "Borrar selección",
    compare_cta: "Comparar {n} alimentos",
    compare_title: "Comparación de alimentos",
    compare_winner: "Ganador",
    breakfast: "Desayuno",
    lunch: "Almuerzo",
    dinner: "Cena",
    snack: "Merienda",
    meal_added: "Añadido al diario",
    pinned: "Fijado en HI Dashboard",
    unpinned: "Desfijado de HI Dashboard",
    kegg_title: "Rutas metabólicas KEGG",
    kegg_subtitle: "Qué vías biológicas se ven afectadas por las condiciones seleccionadas",
    kegg_show: "Mostrar rutas KEGG",
    kegg_hide: "Ocultar",
    kegg_select_conditions: "Selecciona al menos 1 condición para ver las rutas KEGG.",
    kegg_view_on_kegg: "KEGG",
    menu_generate: "Generar menú semanal con AI",
    menu_generating: "GPT-5.2 está generando…",
    menu_title: "Menú AI — GPT-5.2",
    menu_days_label: "Días",
    menu_meals_label: "Comidas/día",
    menu_no_conditions: "Selecciona al menos 1 condición para generar un menú.",
  },
};

const TONE_COLORS = {
  green:   { bg: "bg-emerald-500/15", text: "text-emerald-600" },
  blue:    { bg: "bg-sky-500/15",     text: "text-sky-600" },
  emerald: { bg: "bg-emerald-500/15", text: "text-emerald-600" },
  pink:    { bg: "bg-pink-500/15",    text: "text-pink-600" },
  cyan:    { bg: "bg-cyan-500/15",    text: "text-cyan-600" },
  violet:  { bg: "bg-violet-500/15",  text: "text-violet-600" },
};

const fmsColor = (fms) => {
  if (fms >= 80) return "from-emerald-400 to-emerald-600";
  if (fms >= 65) return "from-sky-400 to-sky-600";
  if (fms >= 50) return "from-amber-400 to-amber-600";
  return "from-slate-400 to-slate-600";
};

export default function NutritionLabPage() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const lang = language === "en" ? "en" : "bg";
  const t = T[lang];

  // Metadata
  const [meta, setMeta] = useState({ conditions: [], diets: [], indices: [] });
  // iter464 — persist filter state across navigation/reloads
  const STORAGE_KEY = "nutramilo:foodlab:state:v1";
  const restored = (() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}"); }
    catch { return {}; }
  })();
  // Filters
  const [selectedConds, setSelectedConds] = useState(restored.selectedConds || []);
  const [selectedPhens, setSelectedPhens] = useState(restored.selectedPhens || []);
  const [selectedDiets, setSelectedDiets] = useState(restored.selectedDiets || []);
  const [sliders, setSliders] = useState(restored.sliders || {});
  // Recommendations (iter462)
  const [recommendation, setRecommendation] = useState(null);
  const [recommendDismissed, setRecommendDismissed] = useState(false);
  const [showEvidence, setShowEvidence] = useState(false);  // iter478
  // Results
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [poolSize, setPoolSize] = useState(5000);     // iter462 — bigger pool
  const [randomSeed, setRandomSeed] = useState(null); // iter462 — regenerate
  const [activeFood, setActiveFood] = useState(null);
  const [explanation, setExplanation] = useState(null);
  const [explaining, setExplaining] = useState(false);
  // iter503 — Conditions search filter (99 conditions → searchable + scrollable)
  const [condSearch, setCondSearch] = useState("");
  // iter462 Phase 4/5/6
  const [pinnedIds, setPinnedIds] = useState(new Set());        // food IDs pinned to HI
  const [compareIds, setCompareIds] = useState(new Set());      // food IDs selected for compare (max 3)
  const [compareData, setCompareData] = useState(null);          // server response when modal open
  const [comparing, setComparing] = useState(false);
  // iter465 Phase B — KEGG + AI Menu
  const [keggOpen, setKeggOpen] = useState(false);
  const [keggData, setKeggData] = useState(null);
  const [menuDays, setMenuDays] = useState(7);
  const [menuMealsPerDay, setMenuMealsPerDay] = useState(3);
  const [menuLoading, setMenuLoading] = useState(false);
  const [menuData, setMenuData] = useState(null);
  const [menuId, setMenuId] = useState(null);  // iter481
  const [menuProfile, setMenuProfile] = useState(null);  // iter482
  // iter484 — first-visit educational disclaimer (EU AI Act Art. 50 + GDPR Art. 13)
  const [disclaimerOpen, setDisclaimerOpen] = useState(() => !hasFoodlabConsent());
  // iter529 — Grouped view (top 5 per food group) + client-side result search
  const [groupedResults, setGroupedResults] = useState(null);  // {groups:[...], total}
  const [resultSearch, setResultSearch] = useState("");        // live filter

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/foodlab/conditions`).then((r) => r.data),
      axios.get(`${API}/foodlab/diets`).then((r) => r.data),
      axios.get(`${API}/foodlab/indices`).then((r) => r.data),
    ]).then(([c, d, i]) => {
      setMeta({ conditions: c.conditions || [], diets: d.diets || [], indices: i.indices || [] });
      // iter464 — keep restored values; fill any missing index ids with neutral 50
      setSliders((prev) => {
        const merged = { ...prev };
        (i.indices || []).forEach((idx) => {
          if (merged[idx.id] === undefined) merged[idx.id] = 50;
        });
        return merged;
      });
    }).catch(() => toast.error("Грешка при зареждане"));
  }, [API]);

  // iter1017 — профилът захранва лабораторията: ако потребителят още не е
  // избирал състояния, вземаме декларираните в „Моите данни".
  const [profilePrefill, setProfilePrefill] = useState(null);
  useEffect(() => {
    if (!meta.conditions.length) return;
    axios.get(`${API}/profile-signals`, { params: { domain: "nutrition" } })
      .then(({ data }) => {
        const known = new Set(meta.conditions.map((c) => c.id));
        const ids = (data.lab_condition_ids || []).filter((id) => known.has(id));
        setProfilePrefill(ids);
        if (ids.length && !(restored.selectedConds || []).length) {
          setSelectedConds((prev) => (prev.length ? prev : ids));
          toast.success(`Взети са ${ids.length} състояния от профила Ви`);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API, meta.conditions]);

  // iter464 — persist filter state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        selectedConds, selectedPhens, selectedDiets, sliders,
      }));
    } catch {}
  }, [selectedConds, selectedPhens, selectedDiets, sliders]);  // eslint-disable-line react-hooks/exhaustive-deps

  // iter462 Phase 6 — fetch pinned food IDs so we can render Pin/Unpin state
  useEffect(() => {
    axios.get(`${API}/foodlab/recommended`)
      .then(({ data }) => setPinnedIds(new Set((data.items || []).map((f) => f.food_id))))
      .catch(() => {});
  }, [API]);

  // iter464 — auto re-search on mount if user has restored conditions
  // (preserves the "session" across navigation/reloads)
  useEffect(() => {
    if (meta.conditions.length > 0 && (restored.selectedConds || []).length > 0 && !results) {
      runSearch({ offset: 0, seed: null, append: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta.conditions]);

  // iter462 Phase 4 — Save to Diary
  const saveToDiary = async (food, mealType, amount = 100) => {
    try {
      await axios.post(`${API}/foodlab/add-to-diary`, {
        food_name: food.name_bg || food.name_en,
        meal_type: mealType,
        amount_g: amount,
        macros:    food.macros || {},
        source:    "foodlab",
      });
      toast.success(t.meal_added);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || (lang === "bg" ? "Грешка" : "Error"));
      return false;
    }
  };

  // iter462 Phase 4 — Add to Plate (saves as snack + redirects to diary)
  const addToPlate = async (food) => {
    const ok = await saveToDiary(food, "snack", 100);
    if (ok) {
      setActiveFood(null);
      setTimeout(() => navigate("/dashboard/diary"), 350);
    }
  };

  // iter462 Phase 6 — Pin/Unpin to HI Dashboard
  const togglePin = async (food) => {
    if (pinnedIds.has(food.id)) {
      try {
        await axios.delete(`${API}/foodlab/recommended/${encodeURIComponent(food.id)}`);
        setPinnedIds((s) => { const n = new Set(s); n.delete(food.id); return n; });
        toast.success(t.unpinned);
      } catch {
        toast.error(lang === "bg" ? "Грешка" : "Error");
      }
      return;
    }
    try {
      await axios.post(`${API}/foodlab/recommended/save`, {
        food_id:    food.id,
        name_bg:    food.name_bg,
        name_en:    food.name_en,
        fms:        food.fms,
        macros:     food.macros || {},
        indices:    food.indices || {},
        components: food.components || {},
        badges:     food.badges || [],
        conditions: selectedConds,
      });
      setPinnedIds((s) => new Set(s).add(food.id));
      toast.success(t.pinned);
    } catch {
      toast.error(lang === "bg" ? "Грешка" : "Error");
    }
  };

  // iter462 Phase 5 — Compare selection
  const toggleCompare = (foodId) => {
    setCompareIds((s) => {
      const n = new Set(s);
      if (n.has(foodId)) { n.delete(foodId); return n; }
      if (n.size >= 3) {
        toast.error(lang === "bg" ? "Можеш да сравниш максимум 3 храни" : "Max 3 foods");
        return s;
      }
      n.add(foodId);
      return n;
    });
  };

  const runCompare = async () => {
    if (!results?.results || compareIds.size < 2) return;
    const foods = results.results.filter((f) => compareIds.has(f.id));
    setComparing(true);
    try {
      const { data } = await axios.post(`${API}/foodlab/compare`, { foods });
      setCompareData({ ...data, foods });
    } catch (e) {
      toast.error(e?.response?.data?.detail || (lang === "bg" ? "Грешка" : "Error"));
    } finally {
      setComparing(false);
    }
  };

  // iter465 Phase B — KEGG pathway lookup (auto on conditions change while panel open)
  useEffect(() => {
    if (!keggOpen || selectedConds.length === 0) {
      if (!keggOpen) setKeggData(null);
      return;
    }
    Promise.all([
      axios.post(`${API}/foodlab/kegg-pathways`, { conditions: selectedConds }),
      axios.post(`${API}/foodlab/kegg-modulators`, { conditions: selectedConds }),
    ])
      .then(([pathRes, modRes]) => setKeggData({ ...pathRes.data, modulators: modRes.data?.modulators || [] }))
      .catch(() => setKeggData({ pathways: [], modulators: [] }));
  }, [API, keggOpen, selectedConds]);

  // iter465 Phase B — AI menu generator (Claude Sonnet 4.5)
  // iter467 — Refactored to async job pattern to avoid 60s K8s ingress timeout.
  // POST /generate-menu/start returns job_id; we poll /status/{job_id}.
  const generateMenu = async () => {
    if (selectedConds.length === 0) {
      toast.error(t.menu_no_conditions);
      return;
    }
    setMenuLoading(true);
    setMenuData(null);
    try {
      const payload = {
        days: menuDays,
        meals_per_day: menuMealsPerDay,
        conditions: selectedConds,
        diets: selectedDiets,
        dil_target_g: Math.max(30, 70 - (sliders.dil ?? 50) * 0.4),
        nis_target_pct: Math.max(15, 45 - (sliders.nis ?? 50) * 0.2),
        col3_target: sliders.col3 ?? 60,
        pinned_food_ids: Array.from(pinnedIds),
        use_personal_dil_budget: true,  // iter475 — sync with user's personal DIL budget
      };
      const startRes = await axios.post(`${API}/foodlab/generate-menu/start`, payload, { timeout: 20000 });
      const jobId = startRes.data?.job_id;
      if (!jobId) throw new Error("no job id");

      // Poll every 4s for up to 4 minutes.
      // iter473 — Per-poll timeouts are tolerated (network blips); only
      // bail when the overall 4-minute budget runs out.
      const start = Date.now();
      const MAX_MS = 240000; // 4 min
      let finished = null;
      let consecutiveErrors = 0;
      while (Date.now() - start < MAX_MS) {
        await new Promise((r) => setTimeout(r, 4000));
        try {
          const statusRes = await axios.get(`${API}/foodlab/generate-menu/status/${jobId}`, { timeout: 30000 });
          consecutiveErrors = 0;
          const s = statusRes.data?.status;
          if (s === "done") {
            finished = statusRes.data;
            break;
          }
          if (s === "failed") {
            throw new Error(statusRes.data?.error || "generation failed");
          }
        } catch (pollErr) {
          // Network/timeout blip — keep polling unless we've hit 5 in a row.
          if (pollErr?.response?.data?.detail || pollErr?.message === "generation failed") {
            throw pollErr;
          }
          consecutiveErrors += 1;
          if (consecutiveErrors >= 5) {
            throw new Error(lang === "bg" ? "Връзката е нестабилна. Опитай отново." : "Connection unstable. Try again.");
          }
        }
      }
      if (!finished) throw new Error(lang === "bg" ? "Генерирането отнема твърде дълго. Опитай с по-малко дни." : "Generation took too long. Try fewer days.");
      setMenuData(finished.menu);
      setMenuId(finished.menu_id || null);  // iter481
      setMenuProfile(finished.profile_applied || null);  // iter482
      toast.success(lang === "bg" ? "Менюто е готово" : "Menu ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || (lang === "bg" ? "Грешка при генериране" : "Generation failed"));
    } finally {
      setMenuLoading(false);
    }
  };

  // Auto-pull phenotypes when conditions change
  const availablePhens = useMemo(() => {
    const map = new Map();
    meta.conditions.forEach((c) => {
      if (selectedConds.includes(c.id)) {
        (c.phenotypes || []).forEach((p) => { map.set(p.id, p); });
      }
    });
    return Array.from(map.values());
  }, [meta.conditions, selectedConds]);

  // iter462 — auto-recommend diets + sliders when conditions/phenotypes change
  useEffect(() => {
    if (selectedConds.length === 0) {
      setRecommendation(null);
      setRecommendDismissed(false);
      return;
    }
    const ctrl = new AbortController();
    axios.post(
      `${API}/foodlab/recommend-filters`,
      { conditions: selectedConds, phenotypes: selectedPhens },
      { signal: ctrl.signal },
    ).then(({ data }) => {
      setRecommendation(data);
      setRecommendDismissed(false);
    }).catch(() => {});
    return () => ctrl.abort();
  }, [API, selectedConds, selectedPhens]);

  const applyRecommendation = () => {
    if (!recommendation) return;
    if (recommendation.recommended_diets?.length) {
      setSelectedDiets(recommendation.recommended_diets);
    }
    if (recommendation.auto_sliders) {
      setSliders((prev) => ({ ...prev, ...recommendation.auto_sliders }));
    }
    setRecommendDismissed(true);
    toast.success(lang === "bg" ? "Препоръките са приложени" : "Recommendations applied");
  };

  const toggle = (list, setter, val) =>
    setter(list.includes(val) ? list.filter((x) => x !== val) : [...list, val]);

  const runSearch = async ({ offset = 0, seed = null, append = false } = {}) => {
    setSearching(true);
    if (!append) {
      setActiveFood(null);
      setExplanation(null);
      setResultSearch("");  // iter529 — reset live filter on new search
    }
    try {
      const { data } = await axios.post(`${API}/foodlab/search`, {
        conditions:    selectedConds,
        phenotypes:    selectedPhens,
        diets:         selectedDiets,
        index_weights: sliders,
        limit:         20,
        offset,
        pool_size:     poolSize,
        random_seed:   seed,
      });
      if (append && results?.results) {
        setResults({ ...data, results: [...results.results, ...data.results] });
      } else {
        setResults(data);
      }
      // iter529 — Also fetch grouped (top 5 per group) view in parallel.
      if (!append) {
        try {
          const { data: gd } = await axios.post(`${API}/foodlab/search-grouped`, {
            conditions:    selectedConds,
            phenotypes:    selectedPhens,
            diets:         selectedDiets,
            index_weights: sliders,
            per_group:     10,
            pool_size:     Math.max(poolSize, 8000),
            random_seed:   seed,
          });
          setGroupedResults(gd);
        } catch {
          setGroupedResults(null);
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || (lang === "bg" ? "Грешка" : "Error"));
    } finally {
      setSearching(false);
    }
  };

  const [formulasData, setFormulasData] = useState(null);
  const [formulasOpen, setFormulasOpen] = useState(false);

  // iter469 — load formulas reference once for the modal
  useEffect(() => {
    axios.get(`${API}/foodlab/formulas`)
      .then(({ data }) => setFormulasData(data))
      .catch(() => {});
  }, [API]);

  const loadMore = () => {
    if (!results) return;
    runSearch({ offset: results.results.length, seed: randomSeed, append: true });
  };

  const regenerate = () => {
    const newSeed = Math.floor(Math.random() * 1_000_000);
    setRandomSeed(newSeed);
    runSearch({ offset: 0, seed: newSeed, append: false });
  };

  const openFood = async (food) => {
    setActiveFood(food);
    setExplanation(null);
    setExplaining(true);
    try {
      const { data } = await axios.post(`${API}/foodlab/explain`, {
        food_id:    food.id,
        name_en:    food.name_en || food.name_bg || "",
        fms:        food.fms,
        components: food.components,
        conditions: selectedConds,
        phenotypes: selectedPhens,
        diets:      selectedDiets,
        indices:    food.indices,
      });
      setExplanation(data);
    } catch {
      setExplanation({ explanation_bg: "Обяснението не е достъпно в момента." });
    } finally {
      setExplaining(false);
    }
  };

  const card = isDark ? "bg-[hsl(223,47%,9%)] border-[hsl(223,47%,16%)]" : "bg-white border-slate-200 shadow-sm";
  const txt = isDark ? "text-white" : "text-slate-900";
  const sub = isDark ? "text-slate-400" : "text-slate-600";

  return (
    <div className={`min-h-screen ${isDark ? "bg-[hsl(223,47%,6%)]" : "bg-slate-50"}`} data-testid="nutrition-lab-page">
      {/* iter484 — First-visit educational disclaimer (EU AI Act Art. 50 + GDPR Art. 13) */}
      <NutritionLabDisclaimerModal
        open={disclaimerOpen}
        onAccept={() => setDisclaimerOpen(false)}
        onCancel={() => navigate("/")}
        language={lang}
        isDark={isDark}
      />
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-start gap-3 mb-6">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center bg-gradient-to-br from-violet-500 to-cyan-500 shadow-lg">
            <FlaskConical className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className={`text-2xl sm:text-3xl font-bold ${txt}`}>{t.title}</h1>
            <p className={`text-xs sm:text-sm mt-1 ${sub}`}>{t.subtitle}</p>
            {/* iter484 — Re-open educational disclaimer */}
            <button
              type="button"
              onClick={() => setDisclaimerOpen(true)}
              data-testid="foodlab-reopen-disclaimer"
              className={`mt-1.5 inline-flex items-center gap-1 text-[10.5px] underline-offset-2 hover:underline ${isDark ? "text-amber-400/80" : "text-amber-700"}`}
              title={lang === "bg" ? "Виж информация за инструмента" : "Open tool information"}
            >
              <AlertTriangle className="w-3 h-3" />
              {lang === "bg"
                ? "Образователен инструмент — не замества медицински съвет"
                : "Educational tool — not medical advice"}
            </button>
          </div>
          {/* iter486 — Cross-link to Mind & Body Lab */}
          <button
            type="button"
            onClick={() => navigate("/dashboard/mindbody-lab")}
            data-testid="foodlab-to-mindbody"
            className={`hidden sm:flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold shadow ${isDark ? "bg-gradient-to-r from-violet-600/30 to-pink-600/30 text-violet-200 border border-violet-500/40 hover:from-violet-600/40 hover:to-pink-600/40" : "bg-gradient-to-r from-violet-100 to-pink-100 text-violet-800 border border-violet-200 hover:from-violet-200 hover:to-pink-200"}`}
            title={lang === "bg" ? "Mind & Body Lab — движение, сън, стрес" : "Mind & Body Lab — movement, sleep, stress"}
          >
            <span className="w-7 h-7 rounded-lg flex items-center justify-center bg-gradient-to-br from-violet-500 to-pink-500 text-white">
              <Dna className="w-3.5 h-3.5" />
            </span>
            <span className="hidden md:inline">{lang === "bg" ? "Mind & Body Lab" : "Mind & Body Lab"}</span>
            <ChevronRight className="w-3.5 h-3.5 opacity-70" />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* FILTERS — left column */}
          <div className="lg:col-span-4 space-y-4">
            {/* iter906 — Load from Pathway Vault (single source of truth) */}
            <LoadFromVaultBanner
              currentConditions={selectedConds}
              onApply={(ids) => setSelectedConds(ids)}
              isDark={isDark}
              lang={lang}
              testid="foodlab-load-from-vault"
            />
            {/* iter907 — Polyphenol classes hitting user's pathways */}
            <MyPolyphenolsCard isDark={isDark} lang={lang} testid="foodlab-my-polyphenols" />
            {/* iter503 — How to start (collapsible) */}
            <HowToStartCard
              storageKey="nutramilo_howto_nutrition_lab"
              isDark={isDark}
              accent="from-violet-500 to-cyan-500"
              testid="howto-nutrition-lab"
              title={lang === "bg" ? "Как работи Хранителната лаборатория" : "How Nutrition Lab works"}
              subtitle={lang === "bg"
                ? "Избираме храните, които работят с Вашето тяло, а не срещу него."
                : "AI picks foods that work with your body, not against it."}
              benefits={lang === "bg"
                ? ["Подредени храни", "Меню за 7 дни", "Биологични пътища (KEGG)", "Български продукти"]
                : ["AI-ranked foods", "AI 7-day menu", "KEGG pathways", "Local foods"]}
              steps={lang === "bg" ? [
                { title: "1. Изберете до три състояния",
                  body: "Започнете с най-важните за Вас (например поликистозни яйчници, инсулинова резистентност). Съветът ни: едно до три, не повече." },
                { title: "2. Прегледайте намерените обменни типове",
                  body: "Извеждаме подтиповете на обмяната Ви от състоянията. Можете да ги поправите." },
                { title: "3. Натиснете „Намиране на храни“",
                  body: "Получавате подредени храни според съответствието (FMS) и обяснение защо точно те." },
                { title: "4. По желание — меню за седем дни",
                  body: "Лично меню по препоръките на ESPEN и EFSA, готово за отпечатване." },
              ] : [
                { title: "1. Pick up to 3 conditions",
                  body: "Start with the most important (e.g. PCOS, insulin resistance). Tip: 1-3, no more." },
                { title: "2. Review auto-detected phenotypes",
                  body: "We infer your metabolic subtypes from conditions. Adjust if needed." },
                { title: "3. Hit \"Find foods\"",
                  body: "You get foods ranked by Food Match Score (FMS) with AI explanations." },
                { title: "4. (Optional) Generate a 7-day AI menu",
                  body: "Personalized plan following ESPEN / EFSA, ready to print." },
              ]}
            />

            {/* iter462 — Smart recommendation banner */}
            {recommendation && !recommendDismissed && selectedConds.length > 0 && (
              <section
                data-testid="foodlab-recommend-banner"
                className={`rounded-2xl border p-3.5 relative overflow-hidden ${
                  isDark
                    ? "bg-gradient-to-br from-violet-900/30 to-cyan-900/30 border-violet-700/50"
                    : "bg-gradient-to-br from-violet-50 to-cyan-50 border-violet-200"
                }`}
              >
                <div className="flex items-start gap-2 mb-2">
                  <Wand2 className="w-4 h-4 text-violet-500 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-xs font-bold ${txt}`}>{t.recommend_title}</h3>
                    <p className={`text-[11px] mt-1 leading-snug ${sub}`}>
                      {lang === "bg" ? recommendation.rationale_bg : recommendation.rationale_en}
                    </p>
                  </div>
                  <button
                    onClick={() => setRecommendDismissed(true)}
                    data-testid="foodlab-recommend-dismiss"
                    className={`shrink-0 p-1 rounded-full ${sub} hover:opacity-70`}
                    aria-label={t.recommend_dismiss}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
                <button
                  onClick={applyRecommendation}
                  data-testid="foodlab-recommend-apply"
                  className="w-full mt-1 py-2 rounded-lg text-white text-xs font-semibold flex items-center justify-center gap-1.5 bg-gradient-to-r from-violet-600 to-cyan-600 hover:opacity-90"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  {t.recommend_apply}
                </button>
                {/* iter478 — "Why these values?" transparency button */}
                {recommendation.evidence && recommendation.evidence.length > 0 && (
                  <button
                    onClick={() => setShowEvidence(true)}
                    data-testid="foodlab-recommend-why"
                    className={`w-full mt-1.5 py-1.5 rounded-lg text-[10.5px] font-semibold flex items-center justify-center gap-1.5 border transition-colors ${
                      isDark
                        ? "border-violet-500/40 text-violet-200 hover:bg-violet-500/15"
                        : "border-violet-300/70 text-violet-700 hover:bg-violet-100/70"
                    }`}
                  >
                    <Info className="w-3.5 h-3.5" />
                    {lang === "bg" ? "Защо точно тези стойности? (научни източници)" : "Why these values? (sources)"}
                  </button>
                )}
              </section>
            )}

            {/* iter1017 — какво знае профилът */}
            <ProfileContextStrip domain="nutrition" isDark={isDark} className="mb-3" />
            {!!profilePrefill?.length && (
              <button
                onClick={() => setSelectedConds(profilePrefill)}
                data-testid="nutrition-apply-profile"
                className="mb-3 w-full px-3 py-2 rounded-lg text-[11px] font-bold
                           bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/25"
              >
                Приложи състоянията от профила ({profilePrefill.length})
              </button>
            )}

            {/* Conditions — iter503: search + scroll (99 conditions) */}
            <FilterSection
              title={`${t.section_conditions} (${selectedConds.length}${meta.conditions.length ? ` / ${meta.conditions.length}` : ""})`}
              icon={HeartPulse}
              card={card}
              txt={txt}
              sub={sub}
            >
              {/* Search input */}
              <div className="relative mb-2">
                <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${sub}`} />
                <input
                  type="text"
                  value={condSearch}
                  onChange={(e) => setCondSearch(e.target.value)}
                  placeholder={lang === "bg" ? "Търси състояние…" : "Search a condition…"}
                  data-testid="condition-search-input"
                  className={`w-full pl-8 pr-7 py-1.5 text-[11px] rounded-lg border outline-none transition ${
                    isDark
                      ? "bg-slate-950 border-slate-700 text-slate-200 focus:border-violet-500"
                      : "bg-white border-slate-200 text-slate-800 focus:border-violet-500"
                  }`}
                />
                {condSearch && (
                  <button
                    onClick={() => setCondSearch("")}
                    data-testid="condition-search-clear"
                    className={`absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full flex items-center justify-center ${
                      isDark ? "bg-slate-700 hover:bg-slate-600 text-white" : "bg-slate-200 hover:bg-slate-300 text-slate-700"
                    }`}
                    aria-label="Clear search"
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                )}
              </div>

              {/* Selected pills (always visible, even when scrolled away in list) */}
              {selectedConds.length > 0 && (
                <div
                  className={`mb-2 p-1.5 rounded-lg flex flex-wrap gap-1 ${
                    isDark ? "bg-violet-500/5 border border-violet-500/20" : "bg-violet-50 border border-violet-200"
                  }`}
                  data-testid="selected-conditions-pills"
                >
                  {selectedConds.map((cid) => {
                    const c = meta.conditions.find((x) => x.id === cid);
                    if (!c) return null;
                    return (
                      <button
                        key={cid}
                        onClick={() => toggle(selectedConds, setSelectedConds, cid)}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-violet-600 text-white hover:bg-violet-700 transition"
                      >
                        {lang === "bg" ? c.name_bg : c.name_en}
                        <X className="w-2.5 h-2.5" />
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Scrollable list */}
              <div
                className={`grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-72 overflow-y-auto pr-1 ${
                  isDark ? "scrollbar-thumb-slate-700" : "scrollbar-thumb-slate-300"
                }`}
                data-testid="conditions-scroll-list"
              >
                {(() => {
                  const q = condSearch.trim().toLowerCase();
                  const filtered = meta.conditions.filter((c) => {
                    if (!q) return true;
                    return (
                      (c.name_bg || "").toLowerCase().includes(q) ||
                      (c.name_en || "").toLowerCase().includes(q) ||
                      (c.id || "").toLowerCase().includes(q)
                    );
                  });
                  if (filtered.length === 0) {
                    return (
                      <div className={`col-span-full text-center py-4 text-[11px] ${sub}`}>
                        {lang === "bg" ? "Няма съвпадения." : "No matches."}
                      </div>
                    );
                  }
                  return filtered.map((c) => {
                    const active = selectedConds.includes(c.id);
                    return (
                      <button
                        key={c.id}
                        onClick={() => toggle(selectedConds, setSelectedConds, c.id)}
                        data-testid={`condition-${c.id}`}
                        className={`text-left px-2 py-1.5 rounded-lg text-[11px] border transition-all ${
                          active
                            ? "border-violet-500 bg-violet-500/10 text-violet-600 font-semibold"
                            : (isDark ? "border-slate-700 text-slate-300 hover:border-slate-500" : "border-slate-200 text-slate-700 hover:border-slate-400")
                        }`}
                      >
                        {lang === "bg" ? c.name_bg : c.name_en}
                      </button>
                    );
                  });
                })()}
              </div>
            </FilterSection>

            {/* Phenotypes — only render if any conditions selected */}
            {availablePhens.length > 0 && (
              <FilterSection title={t.section_phenotypes} icon={Activity} card={card} txt={txt} sub={sub}>
                <div className="flex flex-wrap gap-1.5">
                  {availablePhens.map((p) => {
                    const active = selectedPhens.includes(p.id);
                    return (
                      <button
                        key={p.id}
                        onClick={() => toggle(selectedPhens, setSelectedPhens, p.id)}
                        data-testid={`phenotype-${p.id}`}
                        className={`px-2.5 py-1 rounded-full text-[11px] border transition-all ${
                          active
                            ? "border-cyan-500 bg-cyan-500/10 text-cyan-600 font-semibold"
                            : (isDark ? "border-slate-700 text-slate-300" : "border-slate-200 text-slate-700")
                        }`}
                      >
                        {lang === "bg" ? p.label_bg : p.label_en}
                      </button>
                    );
                  })}
                </div>
              </FilterSection>
            )}

            {/* Diets */}
            <FilterSection title={`${t.section_diets} (${selectedDiets.length}/${meta.diets.length})`} icon={Salad} card={card} txt={txt} sub={sub}>
              <p className={`text-[10px] mb-2 ${sub}`}>
                {lang === "bg"
                  ? `Налични са ${meta.diets.length} диетични модела. Активираните се ползват в препоръките и AI меню.`
                  : `${meta.diets.length} dietary patterns available. Active ones are used for recommendations and AI menu.`}
              </p>
              <div className="grid grid-cols-3 gap-1.5">
                {meta.diets.map((d) => {
                  const active = selectedDiets.includes(d.id);
                  return (
                    <button
                      key={d.id}
                      onClick={() => toggle(selectedDiets, setSelectedDiets, d.id)}
                      data-testid={`diet-${d.id}`}
                      className={`px-2 py-1.5 rounded-lg text-[11px] border transition-all text-center ${
                        active
                          ? "border-emerald-500 bg-emerald-500/10 text-emerald-600 font-semibold"
                          : (isDark ? "border-slate-700 text-slate-300" : "border-slate-200 text-slate-700")
                      }`}
                    >
                      <div className="text-base mb-0.5">{d.icon}</div>
                      {lang === "bg" ? d.name_bg : d.name_en}
                    </button>
                  );
                })}
              </div>
            </FilterSection>

            {/* Sliders */}
            <FilterSection
              title={t.section_indices}
              icon={Sliders}
              card={card}
              txt={txt}
              sub={sub}
              extraAction={
                <button
                  onClick={() => setFormulasOpen(true)}
                  data-testid="open-formulas-btn"
                  className={`text-[10px] font-semibold hover:underline ${isDark ? "text-violet-300" : "text-violet-600"}`}
                >
                  {lang === "bg" ? "Формули →" : "Formulas →"}
                </button>
              }
            >
              <div className="space-y-2.5">
                {meta.indices.map((idx) => {
                  const recommended = recommendation?.auto_sliders?.[idx.id];
                  const current = sliders[idx.id] ?? 50;
                  const isAdaptedHigh = recommended != null && recommended >= 75;
                  return (
                    <div key={idx.id}>
                      <div className="flex items-center justify-between text-[11px] mb-0.5">
                        <div className="flex items-center gap-1">
                          <span className={txt}>{lang === "bg" ? idx.name_bg : idx.name_en}</span>
                          <IndexInfoTooltip idx={idx} lang={lang} isDark={isDark} recommended={recommended} current={current} conds={selectedConds} t={t} />
                          {isAdaptedHigh && (
                            <span
                              className={`text-[8.5px] px-1 py-0 rounded-full font-bold ${isDark ? "bg-violet-500/25 text-violet-300" : "bg-violet-100 text-violet-700"}`}
                              title={lang === "bg" ? `Препоръчано за избраните състояния: ${recommended}` : `Recommended for selected conditions: ${recommended}`}
                            >
                              ★ {recommended}
                            </span>
                          )}
                        </div>
                        <span className={`font-mono ${sub}`}>{current}</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={current}
                        onChange={(e) => setSliders((s) => ({ ...s, [idx.id]: Number(e.target.value) }))}
                        data-testid={`slider-${idx.id}`}
                        className="w-full accent-violet-500"
                      />
                    </div>
                  );
                })}
              </div>
            </FilterSection>

            {/* iter465 Phase B — KEGG metabolic pathways subsection */}
            {selectedConds.length > 0 && (
              <section className={`rounded-2xl border p-3.5 ${card}`} data-testid="kegg-section">
                <button
                  onClick={() => setKeggOpen((o) => !o)}
                  data-testid="kegg-toggle-btn"
                  className="w-full flex items-center justify-between text-left"
                >
                  <div className="flex items-center gap-2">
                    <Dna className="w-3.5 h-3.5 text-violet-500" />
                    <div>
                      <h3 className={`text-[11px] font-bold uppercase tracking-wider ${sub}`}>
                        {t.kegg_title}
                      </h3>
                      <p className={`text-[10px] mt-0.5 leading-tight ${sub}`}>
                        {keggOpen ? t.kegg_subtitle : t.kegg_show}
                      </p>
                    </div>
                  </div>
                  <ChevronDown className={`w-4 h-4 ${sub} transition-transform ${keggOpen ? "rotate-180" : ""}`} />
                </button>
                {keggOpen && (
                  <div className="mt-3 space-y-1.5" data-testid="kegg-pathways-list">
                    {!keggData && (
                      <div className="flex items-center justify-center py-3">
                        <Loader2 className={`w-4 h-4 animate-spin ${sub}`} />
                      </div>
                    )}
                    {keggData && keggData.pathways && keggData.pathways.length === 0 && (
                      <p className={`text-[11px] ${sub}`}>—</p>
                    )}
                    {keggData && (keggData.pathways || []).map((p) => (
                      <a
                        key={p.id}
                        href={p.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`kegg-pathway-${p.id}`}
                        className={`flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-[11px] transition-all hover:scale-[1.01] ${
                          isDark ? "bg-slate-800 hover:bg-slate-700" : "bg-slate-50 hover:bg-slate-100"
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className={`font-semibold ${txt}`}>{lang === "bg" ? p.name_bg : p.name_en}</div>
                          <div className={`font-mono text-[10px] ${sub}`}>{p.id}</div>
                        </div>
                        <ExternalLink className={`w-3 h-3 shrink-0 ${sub}`} />
                      </a>
                    ))}

                    {/* iter467 — Food modulators per pathway */}
                    {keggData && (keggData.modulators || []).length > 0 && (
                      <div className="mt-3 pt-3 border-t border-slate-500/20 space-y-2">
                        <h4 className={`text-[10px] font-bold uppercase tracking-wider ${sub}`}>
                          {lang === "bg" ? "Храни → пътища (peer-reviewed)" : "Food → pathway modulators"}
                        </h4>
                        {keggData.modulators.map((m) => (
                          <div
                            key={m.pathway_id}
                            data-testid={`kegg-modulator-${m.pathway_id}`}
                            className={`rounded-md p-2 text-[10.5px] ${isDark ? "bg-slate-900/60" : "bg-white"} border ${isDark ? "border-slate-700" : "border-slate-200"}`}
                          >
                            <div className={`font-semibold ${txt} mb-1`}>
                              {lang === "bg" ? m.name_bg : m.name_en}
                              <span className={`ml-1.5 font-mono text-[9.5px] ${sub}`}>{m.pathway_id}</span>
                            </div>
                            {(m.down || []).length > 0 && (
                              <div className="space-y-0.5 mb-1">
                                {m.down.map((f, i) => (
                                  <div key={i} className={`flex items-start gap-1 ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
                                    <span className="font-bold shrink-0">↓</span>
                                    <div className="flex-1">
                                      <span className="font-semibold">{f.food}</span>
                                      {f.evidence_bg && <span className={`block italic ${sub} text-[9.5px]`}>{f.evidence_bg}</span>}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            {(m.up || []).length > 0 && (
                              <div className="space-y-0.5">
                                {m.up.map((f, i) => (
                                  <div key={i} className={`flex items-start gap-1 ${isDark ? "text-rose-300" : "text-rose-700"}`}>
                                    <span className="font-bold shrink-0">↑</span>
                                    <div className="flex-1">
                                      <span className="font-semibold">{f.food}</span>
                                      {f.evidence_bg && <span className={`block italic ${sub} text-[9.5px]`}>{f.evidence_bg}</span>}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </section>
            )}

            {/* CTA */}
            <button
              onClick={() => { setRandomSeed(null); runSearch({ offset: 0, seed: null, append: false }); }}
              disabled={searching}
              data-testid="foodlab-search-btn"
              className="w-full py-3 rounded-xl text-white font-semibold flex items-center justify-center gap-2 bg-gradient-to-r from-violet-600 to-cyan-600 hover:opacity-90 disabled:opacity-50"
            >
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {t.cta}
            </button>

            {/* Pool size selector */}
            <div className={`flex items-center justify-between rounded-xl border px-3 py-2 ${card}`}>
              <span className={`text-[11px] ${sub}`}>{t.pool_label}</span>
              <select
                value={poolSize}
                onChange={(e) => setPoolSize(Number(e.target.value))}
                data-testid="foodlab-pool-size"
                className={`text-xs rounded-md border px-2 py-1 ${isDark ? "bg-slate-800 border-slate-700 text-white" : "bg-white border-slate-200 text-slate-900"}`}
              >
                <option value={1000}>1 000</option>
                <option value={2500}>2 500</option>
                <option value={5000}>5 000</option>
                <option value={7500}>7 500</option>
                <option value={10000}>10 000</option>
              </select>
            </div>

            {/* iter465 — AI menu generator */}
            <section
              data-testid="ai-menu-section"
              className={`rounded-2xl border p-3.5 ${
                isDark
                  ? "bg-gradient-to-br from-pink-900/30 to-violet-900/30 border-pink-700/50"
                  : "bg-gradient-to-br from-pink-50 to-violet-50 border-pink-200"
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <ChefHat className="w-4 h-4 text-pink-500" />
                <h3 className={`text-[11px] font-bold uppercase tracking-wider ${txt}`}>
                  {t.menu_title}
                </h3>
              </div>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <div>
                  <label className={`block text-[10px] font-bold uppercase mb-1 ${sub}`}>{t.menu_days_label}</label>
                  <select
                    value={menuDays}
                    onChange={(e) => setMenuDays(Number(e.target.value))}
                    data-testid="menu-days-select"
                    className={`w-full text-xs rounded-md border px-2 py-1.5 ${isDark ? "bg-slate-900 border-slate-700 text-white" : "bg-white border-slate-300 text-slate-900"}`}
                  >
                    {[1, 2, 3, 5, 7, 14].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div>
                  <label className={`block text-[10px] font-bold uppercase mb-1 ${sub}`}>{t.menu_meals_label}</label>
                  <select
                    value={menuMealsPerDay}
                    onChange={(e) => setMenuMealsPerDay(Number(e.target.value))}
                    data-testid="menu-meals-select"
                    className={`w-full text-xs rounded-md border px-2 py-1.5 ${isDark ? "bg-slate-900 border-slate-700 text-white" : "bg-white border-slate-300 text-slate-900"}`}
                  >
                    {[2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              </div>
              <button
                onClick={generateMenu}
                disabled={menuLoading || selectedConds.length === 0}
                data-testid="generate-menu-btn"
                className="w-full py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 bg-gradient-to-r from-pink-500 to-violet-500 text-white hover:opacity-90 disabled:opacity-50"
              >
                {menuLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                {menuLoading ? t.menu_generating : t.menu_generate}
              </button>
              {selectedConds.length === 0 && (
                <p className={`mt-1.5 text-[10px] text-center ${sub}`}>{t.menu_no_conditions}</p>
              )}
              {/* iter466 — Link to history */}
              <a
                href="/dashboard/my-menus"
                data-testid="link-my-menus"
                className={`mt-2 block text-center text-[10.5px] hover:underline ${sub}`}
              >
                {lang === "bg" ? "→ Моите AI менюта" : "→ My AI menus"}
              </a>
            </section>
          </div>

          {/* RESULTS — right column */}
          <div className="lg:col-span-8">
            {!results && !searching && (
              <div className={`rounded-2xl border p-10 text-center ${card}`}>
                <Sparkles className={`w-10 h-10 mx-auto mb-4 ${sub}`} />
                <p className={`text-sm ${sub}`}>
                  {lang === "bg" ? "Избери филтри и натисни бутона за откриване." : "Pick filters and discover foods."}
                </p>
              </div>
            )}
            {searching && (
              <div className={`rounded-2xl border p-10 text-center ${card}`}>
                <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-violet-500" />
                <p className={`text-sm ${sub}`}>{t.searching.replace("{n}", poolSize.toLocaleString())}</p>
              </div>
            )}
            {results && results.results && results.results.length === 0 && (
              <div className={`rounded-2xl border p-10 text-center ${card}`}>
                <p className={`text-sm ${sub}`}>{t.no_results}</p>
              </div>
            )}
            {results && results.results && results.results.length > 0 && (
              <>
                <div className="flex items-center justify-between mb-3">
                  <h2 className={`text-base font-bold ${txt}`}>{t.results_title}</h2>
                  <span className={`text-[11px] ${sub}`}>{t.results_total.replace("{n}", results.total)}</span>
                </div>

                {/* iter488 — Active phenotypes pill bar (auto-resolved from conditions) */}
                {availablePhens.length > 0 && (
                  <div data-testid="active-phenotypes-bar" className={`mb-3 flex flex-wrap items-center gap-1.5 px-3 py-2 rounded-xl border ${isDark ? "bg-violet-500/10 border-violet-500/30" : "bg-violet-50 border-violet-200"}`}>
                    <Dna className="w-3.5 h-3.5 text-violet-500" />
                    <span className={`text-[10.5px] font-bold uppercase tracking-wider ${isDark ? "text-violet-200" : "text-violet-800"}`}>
                      {lang === "bg" ? "Активни фенотипи (авто):" : "Active phenotypes (auto):"}
                    </span>
                    {availablePhens.slice(0, 6).map((p) => {
                      const isActive = selectedPhens.includes(p.id);
                      return (
                        <span
                          key={p.id}
                          title={p.name_bg || p.name_en}
                          className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                            isActive
                              ? (isDark ? "bg-violet-500 text-white" : "bg-violet-600 text-white")
                              : (isDark ? "bg-violet-500/20 text-violet-200 border border-violet-500/40" : "bg-white text-violet-700 border border-violet-300")
                          }`}
                        >
                          {lang === "bg" ? p.name_bg : p.name_en}
                        </span>
                      );
                    })}
                    {availablePhens.length > 6 && (
                      <span className={`text-[10px] ${sub}`}>+{availablePhens.length - 6}</span>
                    )}
                    <span className={`text-[9.5px] ml-1 ${sub} italic`}>
                      {lang === "bg" ? "(автоматично свързани със състоянията Ви)" : "(auto-linked to your conditions)"}
                    </span>
                  </div>
                )}

                {/* iter472 — Picked-for-menu sticky banner */}
                {pinnedIds.size > 0 && (
                  <div
                    data-testid="picked-for-menu-banner"
                    className={`sticky top-2 z-30 mb-3 rounded-xl border-2 border-violet-400/60 ${
                      isDark ? "bg-violet-950/80" : "bg-violet-50"
                    } backdrop-blur px-3 py-2 flex items-center justify-between gap-2 shadow-sm`}
                  >
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-violet-500" />
                      <span className={`text-xs font-semibold ${isDark ? "text-violet-200" : "text-violet-800"}`}>
                        {t.picked_count.replace("{n}", pinnedIds.size)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          Promise.all(
                            Array.from(pinnedIds).map((id) =>
                              axios.delete(`${API}/foodlab/recommended/${encodeURIComponent(id)}`).catch(() => null)
                            )
                          ).finally(() => {
                            setPinnedIds(new Set());
                            toast.success(t.picks_cleared);
                          });
                        }}
                        data-testid="picks-clear-btn"
                        className={`text-[11px] underline ${isDark ? "text-violet-300" : "text-violet-700"}`}
                      >
                        {t.clear_picks}
                      </button>
                      <button
                        onClick={generateMenu}
                        disabled={menuLoading}
                        data-testid="picks-generate-menu-btn"
                        className="text-[11px] font-bold px-3 py-1.5 rounded-lg bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white hover:opacity-90 disabled:opacity-50"
                      >
                        {menuLoading ? "…" : t.generate_from_picks}
                      </button>
                    </div>
                  </div>
                )}

                {/* iter529 — Live search input + grouped layout */}
                <div className={`relative mb-3 ${card} rounded-xl border`}>
                  <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${sub}`} />
                  <input
                    type="text"
                    value={resultSearch}
                    onChange={(e) => setResultSearch(e.target.value)}
                    placeholder={lang === "bg" ? "Търси в избраните храни…" : "Search selected foods…"}
                    data-testid="results-search-input"
                    className={`w-full pl-9 pr-9 py-2.5 text-sm rounded-xl outline-none bg-transparent ${txt}`}
                  />
                  {resultSearch && (
                    <button
                      onClick={() => setResultSearch("")}
                      data-testid="results-search-clear"
                      className={`absolute right-2.5 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full flex items-center justify-center ${
                        isDark ? "bg-slate-700 hover:bg-slate-600 text-white" : "bg-slate-200 hover:bg-slate-300 text-slate-700"
                      }`}
                      aria-label="Clear search"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>

                {(() => {
                  const q = resultSearch.trim().toLowerCase();
                  const matchFood = (f) => {
                    if (!q) return true;
                    const nb = (f.name_bg || "").toLowerCase();
                    const ne = (f.name_en || "").toLowerCase();
                    return nb.includes(q) || ne.includes(q);
                  };

                  // GROUPED VIEW (top 5 per food group) — default when groupedResults loaded.
                  if (groupedResults && groupedResults.groups && groupedResults.groups.length > 0) {
                    const visibleGroups = groupedResults.groups
                      .map((g) => ({ ...g, items: g.items.filter(matchFood) }))
                      .filter((g) => g.items.length > 0);
                    if (visibleGroups.length === 0) {
                      return (
                        <div className={`rounded-2xl border p-6 text-center ${card}`}>
                          <p className={`text-sm ${sub}`}>
                            {lang === "bg" ? "Няма съвпадения за \"" + resultSearch + "\"" : "No matches for \"" + resultSearch + "\""}
                          </p>
                        </div>
                      );
                    }
                    return (
                      <div data-testid="grouped-results" className="space-y-5">
                        {visibleGroups.map((g) => (
                          <section key={g.id} data-testid={`group-${g.id}`}>
                            <div className="flex items-center justify-between mb-2">
                              <h3 className={`text-sm font-bold flex items-center gap-2 ${txt}`}>
                                <span className="text-lg">{g.icon}</span>
                                {lang === "bg" ? g.name_bg : g.name_en}
                                <span className={`text-[10.5px] font-normal ${sub}`}>
                                  ({g.items.length}/{g.total_in_group})
                                </span>
                              </h3>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              {g.items.map((food) => (
                                <FoodCard
                                  key={food.id}
                                  food={food}
                                  lang={lang}
                                  card={card}
                                  txt={txt}
                                  sub={sub}
                                  onClick={() => openFood(food)}
                                  isDark={isDark}
                                  isPinned={pinnedIds.has(food.id)}
                                  isComparing={compareIds.has(food.id)}
                                  onTogglePin={(e) => { e.stopPropagation(); togglePin(food); }}
                                  onToggleCompare={(e) => { e.stopPropagation(); toggleCompare(food.id); }}
                                  t={t}
                                />
                              ))}
                            </div>
                          </section>
                        ))}
                      </div>
                    );
                  }

                  // FLAT VIEW fallback (older endpoint or filtered to none) — keep existing UX.
                  const flatFiltered = (results.results || []).filter(matchFood);
                  if (flatFiltered.length === 0) {
                    return (
                      <div className={`rounded-2xl border p-6 text-center ${card}`}>
                        <p className={`text-sm ${sub}`}>
                          {lang === "bg" ? "Няма съвпадения." : "No matches."}
                        </p>
                      </div>
                    );
                  }
                  return (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {flatFiltered.map((food) => (
                        <FoodCard
                          key={food.id}
                          food={food}
                          lang={lang}
                          card={card}
                          txt={txt}
                          sub={sub}
                          onClick={() => openFood(food)}
                          isDark={isDark}
                          isPinned={pinnedIds.has(food.id)}
                          isComparing={compareIds.has(food.id)}
                          onTogglePin={(e) => { e.stopPropagation(); togglePin(food); }}
                          onToggleCompare={(e) => { e.stopPropagation(); toggleCompare(food.id); }}
                          t={t}
                        />
                      ))}
                    </div>
                  );
                })()}

                {/* iter462 Phase 5 — Compare CTA */}
                {compareIds.size >= 2 && (
                  <div className="mt-4">
                    <button
                      onClick={runCompare}
                      disabled={comparing}
                      data-testid="foodlab-compare-btn"
                      className="w-full py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-500 to-blue-500 text-white hover:opacity-90 disabled:opacity-50"
                    >
                      {comparing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Scale className="w-4 h-4" />}
                      {t.compare_cta.replace("{n}", compareIds.size)}
                    </button>
                  </div>
                )}

                {/* iter462 — Load more + Regenerate */}
                <div className="mt-4 flex flex-col sm:flex-row gap-2">
                  {results.results.length < results.total && (
                    <button
                      onClick={loadMore}
                      disabled={searching}
                      data-testid="foodlab-load-more-btn"
                      className={`flex-1 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 border transition-all ${
                        isDark ? "border-slate-700 text-slate-200 hover:bg-slate-800" : "border-slate-300 text-slate-700 hover:bg-slate-100"
                      } disabled:opacity-50`}
                    >
                      {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                      {t.load_more}
                    </button>
                  )}
                  <button
                    onClick={regenerate}
                    disabled={searching}
                    data-testid="foodlab-regenerate-btn"
                    className="flex-1 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500 to-pink-500 text-white hover:opacity-90 disabled:opacity-50"
                  >
                    {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCw className="w-4 h-4" />}
                    {t.regenerate}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Detail modal */}
      {activeFood && (
        <FoodDetailModal
          food={activeFood}
          explanation={explanation}
          loading={explaining}
          lang={lang}
          t={t}
          isDark={isDark}
          isPinned={pinnedIds.has(activeFood.id)}
          onSaveToDiary={(mealType, amount) => saveToDiary(activeFood, mealType, amount)}
          onAddToPlate={() => addToPlate(activeFood)}
          onTogglePin={() => togglePin(activeFood)}
          onClose={() => { setActiveFood(null); setExplanation(null); }}
        />
      )}

      {/* iter462 Phase 5 — Compare modal */}
      {compareData && (
        <CompareModal
          data={compareData}
          lang={lang}
          t={t}
          isDark={isDark}
          onClose={() => setCompareData(null)}
        />
      )}

      {/* iter465 Phase B — AI Menu modal (shared component) */}
      {menuData && (
        <MenuModalView
          menu={menuData}
          menuId={menuId}
          profileApplied={menuProfile}
          lang={lang}
          isDark={isDark}
          onClose={() => { setMenuData(null); setMenuId(null); setMenuProfile(null); }}
          onMenuUpdate={(updated) => setMenuData(updated)}
        />
      )}

      {/* iter469 — Formulas reference modal */}
      {formulasOpen && formulasData && (
        <FormulasModal
          data={formulasData}
          lang={lang}
          isDark={isDark}
          onClose={() => setFormulasOpen(false)}
        />
      )}

      {/* iter478 — Evidence ("Why these values?") modal */}
      {showEvidence && recommendation?.evidence && (
        <EvidenceModal
          evidence={recommendation.evidence}
          lang={lang}
          isDark={isDark}
          onClose={() => setShowEvidence(false)}
        />
      )}
    </div>
  );
}

// iter478 — Evidence transparency modal
function EvidenceModal({ evidence, lang, isDark, onClose }) {
  const bg = lang === "bg";
  return (
    <div
      data-testid="evidence-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className={`relative w-full max-w-3xl max-h-[88vh] flex flex-col rounded-2xl shadow-2xl ${isDark ? "bg-slate-900 text-white" : "bg-white text-slate-900"}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-2 rounded-full hover:bg-slate-500/20"
          aria-label="Close"
          data-testid="evidence-modal-close"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="px-6 pt-6 pb-3 pr-12">
          <div className="flex items-center gap-2 mb-1">
            <Info className="w-5 h-5 text-violet-500" />
            <h2 className="text-lg font-bold">
              {bg ? "Защо точно тези стойности?" : "Why these values?"}
            </h2>
          </div>
          <p className={`text-xs mt-1.5 leading-relaxed ${isDark ? "text-slate-400" : "text-slate-600"}`}>
            {bg
              ? "Всеки плъзгач е настроен спрямо избраните състояния и фенотипи, на база peer-reviewed литература."
              : "Each slider is set based on your selected conditions and phenotypes, drawing on peer-reviewed literature."}
          </p>
        </div>
        <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-2.5">
          {evidence.map((row) => (
            <div
              key={row.slider_id}
              data-testid={`evidence-row-${row.slider_id}`}
              className={`rounded-xl border p-3 ${isDark ? "border-slate-700 bg-slate-800/40" : "border-slate-200 bg-slate-50"}`}
            >
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <h3 className="text-sm font-bold">{row.label_bg}</h3>
                <span className="text-xs font-mono px-2 py-0.5 rounded bg-gradient-to-r from-pink-500 to-violet-600 text-white">
                  {row.value}
                </span>
              </div>
              {row.drivers && row.drivers.length > 0 && (
                <div className={`text-[11px] mb-1.5 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                  <span className={`font-semibold ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                    {bg ? "Базирано на: " : "Based on: "}
                  </span>
                  {row.drivers.map((d, i) => (
                    <span key={i}>
                      {i > 0 ? " · " : ""}
                      <span className="font-medium">{d.source_bg}</span>
                      <span className="opacity-60"> ({d.value})</span>
                    </span>
                  ))}
                </div>
              )}
              {row.refs && row.refs.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {row.refs.map((r, i) => (
                    <a
                      key={i}
                      href={r.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`evidence-ref-${row.slider_id}-${i}`}
                      className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border transition-colors ${
                        isDark
                          ? "border-slate-600 text-slate-300 hover:border-slate-400 hover:text-white"
                          : "border-slate-300 text-slate-700 hover:border-slate-500 hover:text-slate-900"
                      }`}
                    >
                      <ExternalLink className="w-2.5 h-2.5" />
                      {r.label}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
          <p className={`text-[10px] mt-3 text-center opacity-60 ${isDark ? "text-slate-500" : "text-slate-500"}`}>
            {bg
              ? "Educational · НЕ е медицински съвет. Стойностите са по подразбиране; можеш ръчно да ги промениш."
              : "Educational · Not medical advice. Defaults shown; you can override manually."}
          </p>
        </div>
      </div>
    </div>
  );
}

// iter468 — Info tooltip / popover next to each Nutramilo slider.
// Shows the metric's definition, ideal range, and how it auto-adjusts for
// the user's selected conditions.
function IndexInfoTooltip({ idx, lang, isDark, recommended, current, conds }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const directionLabel = idx.direction === "lower_better"
    ? (lang === "bg" ? "По-ниско = по-добре" : "Lower is better")
    : (lang === "bg" ? "По-високо = по-добре" : "Higher is better");

  return (
    <span ref={ref} className="relative inline-flex items-center">
      <button
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        data-testid={`slider-info-${idx.id}`}
        aria-label="Info"
        className={`p-0.5 rounded-full transition-colors ${isDark ? "text-slate-500 hover:text-violet-300" : "text-slate-400 hover:text-violet-600"}`}
      >
        <Info className="w-3 h-3" />
      </button>
      {open && (
        <div
          className={`absolute z-30 top-5 left-0 w-72 rounded-xl shadow-2xl border p-3 text-[10.5px] leading-snug ${
            isDark ? "bg-slate-900 border-slate-700 text-slate-200" : "bg-white border-slate-200 text-slate-800"
          }`}
          data-testid={`slider-info-panel-${idx.id}`}
        >
          <div className="flex items-start justify-between gap-2 mb-1.5">
            <h4 className={`text-xs font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
              {lang === "bg" ? idx.name_bg : idx.name_en}
            </h4>
            <span className={`shrink-0 text-[9px] font-mono px-1.5 py-0.5 rounded ${
              idx.direction === "lower_better"
                ? (isDark ? "bg-rose-500/20 text-rose-300" : "bg-rose-100 text-rose-700")
                : (isDark ? "bg-emerald-500/20 text-emerald-300" : "bg-emerald-100 text-emerald-700")
            }`}>
              {directionLabel}
            </span>
          </div>

          <p className="mb-2">{lang === "bg" ? idx.what_bg : (idx.what_en || idx.what_bg)}</p>

          <div className={`grid grid-cols-2 gap-1.5 mb-2 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
            <div>
              <div className="font-bold uppercase text-[9px] opacity-70">{lang === "bg" ? "Идеал" : "Ideal"}</div>
              <div className="font-mono">{lang === "bg" ? idx.ideal_bg : (idx.ideal_en || idx.ideal_bg)}</div>
            </div>
            <div>
              <div className="font-bold uppercase text-[9px] opacity-70">{lang === "bg" ? "Единици" : "Unit"}</div>
              <div className="font-mono">{idx.unit}</div>
            </div>
          </div>

          {idx.high_means_bg && (
            <div className={`mb-1 p-1.5 rounded ${isDark ? "bg-slate-800" : "bg-slate-50"}`}>
              <span className={`font-bold ${idx.direction === "lower_better" ? "text-rose-500" : "text-emerald-500"}`}>↑ </span>
              {lang === "bg" ? idx.high_means_bg : (idx.high_means_en || idx.high_means_bg)}
            </div>
          )}
          {idx.low_means_bg && (
            <div className={`mb-2 p-1.5 rounded ${isDark ? "bg-slate-800" : "bg-slate-50"}`}>
              <span className={`font-bold ${idx.direction === "lower_better" ? "text-emerald-500" : "text-rose-500"}`}>↓ </span>
              {lang === "bg" ? idx.low_means_bg : (idx.low_means_en || idx.low_means_bg)}
            </div>
          )}

          {idx.set_high_for_bg && (
            <div className={`p-1.5 rounded border ${isDark ? "bg-violet-500/10 border-violet-500/30 text-violet-200" : "bg-violet-50 border-violet-200 text-violet-900"}`}>
              <div className="font-bold text-[9.5px] uppercase mb-0.5">
                {lang === "bg" ? "Кога да го вдигнеш" : "When to set high"}
              </div>
              {lang === "bg" ? idx.set_high_for_bg : (idx.set_high_for_en || idx.set_high_for_bg)}
            </div>
          )}

          {recommended != null && conds.length > 0 && (
            <div className={`mt-2 pt-2 border-t flex items-center justify-between gap-2 text-[10px] ${
              isDark ? "border-slate-700" : "border-slate-200"
            }`}>
              <span className={isDark ? "text-slate-400" : "text-slate-600"}>
                {lang === "bg" ? "Препоръка за теб" : "Recommended for you"}
              </span>
              <span className={`font-bold font-mono ${
                Math.abs(current - recommended) < 5
                  ? "text-emerald-500"
                  : (isDark ? "text-amber-300" : "text-amber-600")
              }`}>
                ★ {recommended} <span className="opacity-50">/ {current}</span>
              </span>
            </div>
          )}
        </div>
      )}
    </span>
  );
}


function FilterSection({ title, icon: Icon, card, txt, sub, children, extraAction }) {
  return (
    <section className={`rounded-2xl border p-3.5 ${card}`}>
      <div className="flex items-center justify-between mb-2.5">
        <h3 className={`text-[11px] uppercase tracking-wider font-bold flex items-center gap-1.5 ${sub}`}>
          <Icon className="w-3 h-3" /> {title}
        </h3>
        {extraAction}
      </div>
      {children}
    </section>
  );
}

function FoodCard({ food, lang, card, txt, sub, onClick, isDark, isPinned, isComparing, onTogglePin, onToggleCompare, t }) {
  const name = lang === "bg" ? (food.name_bg || food.name_en) : (food.name_en || food.name_bg);
  return (
    <div
      data-testid={`food-card-${food.id}`}
      className={`relative text-left rounded-xl border p-3 transition-all hover:shadow-md ${card} ${
        isComparing ? "ring-2 ring-indigo-400" : ""
      }`}
    >
      {/* Top action row — pin + compare */}
      <div className="absolute top-2 right-2 flex items-center gap-1.5">
        <button
          onClick={onTogglePin}
          data-testid={`food-pin-${food.id}`}
          aria-label={isPinned ? t.unpin_from_hi : t.pin_to_hi}
          title={isPinned ? t.unpin_from_hi : t.pin_to_hi}
          className={`p-1 rounded-full transition-all ${
            isPinned ? "text-amber-500" : (isDark ? "text-slate-500 hover:text-amber-400" : "text-slate-400 hover:text-amber-500")
          }`}
        >
          <Star className="w-3.5 h-3.5" fill={isPinned ? "currentColor" : "none"} />
        </button>
      </div>

      <button onClick={onClick} className="w-full text-left">
        <div className="flex items-start justify-between mb-2 gap-2 pr-12">
          <h4 className={`text-sm font-semibold flex-1 ${txt}`}>{name}</h4>
          <div className={`px-2 py-1 rounded-lg text-white text-sm font-bold bg-gradient-to-br ${fmsColor(food.fms)}`}>
            {Math.round(food.fms)}
          </div>
        </div>
        <div className="flex flex-wrap gap-1 mb-1.5">
          {(food.badges || []).slice(0, 3).map((b, i) => {
            const tone = TONE_COLORS[b.tone] || TONE_COLORS.blue;
            return (
              <span key={i} className={`text-[10px] px-1.5 py-0.5 rounded-full ${tone.bg} ${tone.text}`}>
                {lang === "bg" ? b.label_bg : b.label_en}
              </span>
            );
          })}
        </div>
        <div className={`text-[10px] flex gap-2 ${sub}`}>
          <span>DIL {food.indices?.dil ?? "—"}</span>
          <span>NIS {food.indices?.nis ?? "—"}</span>
          <span>UIS {food.indices?.uis ?? "—"}</span>
        </div>
      </button>

      {/* Compare toggle — bottom row */}
      <div className="mt-2 flex items-center justify-between gap-2">
        <label
          className={`flex items-center gap-1.5 text-[10.5px] cursor-pointer select-none ${sub}`}
          onClick={onToggleCompare}
          data-testid={`food-compare-${food.id}`}
        >
          <span
            className={`w-3.5 h-3.5 rounded border flex items-center justify-center transition-all ${
              isComparing ? "bg-indigo-500 border-indigo-500" : (isDark ? "border-slate-600" : "border-slate-300")
            }`}
          >
            {isComparing && <Check className="w-2.5 h-2.5 text-white" />}
          </span>
          {t.compare_select}
        </label>

        {/* iter472 — Explicit "for menu" checkbox (uses same pin mechanism) */}
        <label
          className={`flex items-center gap-1.5 text-[10.5px] cursor-pointer select-none font-semibold ${
            isPinned ? "text-violet-500" : sub
          }`}
          onClick={onTogglePin}
          data-testid={`food-for-menu-${food.id}`}
        >
          <span
            className={`w-3.5 h-3.5 rounded border flex items-center justify-center transition-all ${
              isPinned ? "bg-violet-500 border-violet-500" : (isDark ? "border-slate-600" : "border-slate-300")
            }`}
          >
            {isPinned && <Check className="w-2.5 h-2.5 text-white" />}
          </span>
          {t.pick_for_menu}
        </label>
      </div>
    </div>
  );
}

function FoodDetailModal({ food, explanation, loading, lang, t, isDark, isPinned, onSaveToDiary, onAddToPlate, onTogglePin, onClose }) {
  const name = lang === "bg" ? (food.name_bg || food.name_en) : (food.name_en || food.name_bg);
  const [mealType, setMealType] = useState("snack");
  const [amount, setAmount] = useState(100);
  const [savingDiary, setSavingDiary] = useState(false);
  const [savingPlate, setSavingPlate] = useState(false);
  const [pinning, setPinning] = useState(false);

  const handleDiary = async () => {
    setSavingDiary(true);
    await onSaveToDiary(mealType, Number(amount) || 100);
    setSavingDiary(false);
  };
  const handlePlate = async () => {
    setSavingPlate(true);
    await onAddToPlate();
    setSavingPlate(false);
  };
  const handlePin = async () => {
    setPinning(true);
    await onTogglePin();
    setPinning(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className={`relative w-full max-w-2xl max-h-[90vh] flex flex-col rounded-2xl shadow-2xl ${isDark ? "bg-slate-900 text-white" : "bg-white text-slate-900"}`}
        onClick={(e) => e.stopPropagation()}
        data-testid="food-detail-modal"
      >
        <button onClick={onClose} className="absolute top-3 right-3 p-2 rounded-full hover:bg-slate-500/20" aria-label="Close">
          <X className="w-5 h-5" />
        </button>
        <div className="px-6 pt-6 pb-3 shrink-0 pr-12">
          <div className="flex items-start justify-between gap-3 mb-2">
            <h2 className="text-lg font-bold flex-1">{name}</h2>
            <div className={`px-3 py-1.5 rounded-lg text-white text-base font-bold bg-gradient-to-br ${fmsColor(food.fms)}`}>
              {Math.round(food.fms)}/100
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(food.badges || []).map((b, i) => {
              const tone = TONE_COLORS[b.tone] || TONE_COLORS.blue;
              return (
                <span key={i} className={`text-[10px] px-2 py-0.5 rounded-full ${tone.bg} ${tone.text}`}>
                  {lang === "bg" ? b.label_bg : b.label_en}
                </span>
              );
            })}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-5">
          {/* iter462 Phase 4 — Action panel */}
          <section
            className={`rounded-xl p-3 ${isDark ? "bg-slate-800/60 border border-slate-700" : "bg-slate-50 border border-slate-200"}`}
            data-testid="food-action-panel"
          >
            {/* Meal type + serving */}
            <div className="grid grid-cols-2 gap-2 mb-2">
              <div>
                <label className="block text-[10px] font-bold opacity-70 uppercase mb-1">{t.pick_meal_type}</label>
                <select
                  value={mealType}
                  onChange={(e) => setMealType(e.target.value)}
                  data-testid="food-meal-type-select"
                  className={`w-full text-xs rounded-md border px-2 py-1.5 ${isDark ? "bg-slate-900 border-slate-700 text-white" : "bg-white border-slate-300 text-slate-900"}`}
                >
                  <option value="breakfast">{t.breakfast}</option>
                  <option value="lunch">{t.lunch}</option>
                  <option value="dinner">{t.dinner}</option>
                  <option value="snack">{t.snack}</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold opacity-70 uppercase mb-1">{t.serving_g}</label>
                <input
                  type="number"
                  min="1"
                  max="2000"
                  step="10"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  data-testid="food-serving-input"
                  className={`w-full text-xs rounded-md border px-2 py-1.5 ${isDark ? "bg-slate-900 border-slate-700 text-white" : "bg-white border-slate-300 text-slate-900"}`}
                />
              </div>
            </div>
            {/* Action buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <button
                onClick={handleDiary}
                disabled={savingDiary}
                data-testid="food-save-to-diary-btn"
                className="py-2 px-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:opacity-90 disabled:opacity-50"
              >
                {savingDiary ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Notebook className="w-3.5 h-3.5" />}
                {t.save_to_diary}
              </button>
              <button
                onClick={handlePlate}
                disabled={savingPlate}
                data-testid="food-add-to-plate-btn"
                className="py-2 px-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:opacity-90 disabled:opacity-50"
              >
                {savingPlate ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Utensils className="w-3.5 h-3.5" />}
                {t.add_to_plate}
              </button>
              <button
                onClick={handlePin}
                disabled={pinning}
                data-testid="food-pin-hi-btn"
                className={`py-2 px-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 border transition-all disabled:opacity-50 ${
                  isPinned
                    ? "bg-amber-500 text-white border-amber-500"
                    : (isDark ? "border-slate-600 text-slate-200 hover:bg-slate-700" : "border-slate-300 text-slate-700 hover:bg-slate-100")
                }`}
              >
                {pinning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Star className="w-3.5 h-3.5" fill={isPinned ? "currentColor" : "none"} />}
                {isPinned ? t.unpin_from_hi : t.pin_to_hi}
              </button>
            </div>
          </section>

          {/* Components breakdown */}
          <section>
            <h3 className="text-[11px] uppercase tracking-wider font-bold mb-2 opacity-70">
              {lang === "bg" ? "Разбивка на FMS" : "FMS breakdown"}
            </h3>
            <div className="space-y-1.5">
              {Object.entries(food.components || {}).map(([key, val]) => (
                <div key={key}>
                  <div className="flex justify-between text-xs mb-0.5">
                    <span>{t.components[key] || key}</span>
                    <span className="font-mono">{Math.round(val)}%</span>
                  </div>
                  <div className={`h-1.5 rounded-full ${isDark ? "bg-slate-800" : "bg-slate-100"}`}>
                    <div className="h-full rounded-full bg-gradient-to-r from-violet-500 to-cyan-500" style={{ width: `${val}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* AI explanation */}
          <section>
            <h3 className="text-[11px] uppercase tracking-wider font-bold mb-2 opacity-70 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" />
              {t.why_recommended}
            </h3>
            <div className={`rounded-lg p-3 text-sm whitespace-pre-wrap ${isDark ? "bg-slate-800" : "bg-slate-50"}`}>
              {loading ? (
                <div className="flex items-center gap-2 opacity-70">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-xs">{lang === "bg" ? "Генерирам обяснение…" : "Generating…"}</span>
                </div>
              ) : (explanation?.explanation_bg || "—")}
            </div>
          </section>

          {/* Indices table */}
          <section>
            <h3 className="text-[11px] uppercase tracking-wider font-bold mb-2 opacity-70">
              {lang === "bg" ? "Nutramilo индекси" : "Nutramilo indices"}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {Object.entries(food.indices || {}).map(([k, v]) => (
                <div key={k} className={`px-2 py-1.5 rounded-lg ${isDark ? "bg-slate-800" : "bg-slate-50"}`}>
                  <div className="text-[10px] opacity-70 uppercase">{k}</div>
                  <div className="text-sm font-bold">{typeof v === "number" ? v : "—"}</div>
                </div>
              ))}
            </div>
          </section>

          <p className="text-[10px] opacity-60 text-center">{t.disclaimer}</p>
        </div>
      </div>
    </div>
  );
}


// iter462 Phase 5 — side-by-side comparison modal
function CompareModal({ data, lang, t, isDark, onClose }) {
  const foods = data.foods || [];
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className={`relative w-full max-w-4xl max-h-[90vh] flex flex-col rounded-2xl shadow-2xl ${isDark ? "bg-slate-900 text-white" : "bg-white text-slate-900"}`}
        onClick={(e) => e.stopPropagation()}
        data-testid="foodlab-compare-modal"
      >
        <button onClick={onClose} className="absolute top-3 right-3 p-2 rounded-full hover:bg-slate-500/20" aria-label="Close">
          <X className="w-5 h-5" />
        </button>

        <div className="px-6 pt-6 pb-3 shrink-0 pr-12">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <Scale className="w-5 h-5 text-indigo-500" />
            {t.compare_title}
          </h2>
        </div>

        <div className="flex-1 overflow-y-auto px-6 pb-6">
          {/* Header row — food names + FMS */}
          <div className={`grid gap-3 mb-4`} style={{ gridTemplateColumns: `120px repeat(${foods.length}, minmax(0,1fr))` }}>
            <div></div>
            {foods.map((f) => {
              const name = lang === "bg" ? (f.name_bg || f.name_en) : (f.name_en || f.name_bg);
              return (
                <div key={f.id} className={`rounded-lg p-2 text-center ${isDark ? "bg-slate-800" : "bg-slate-50"}`}>
                  <div className="text-xs font-semibold line-clamp-2 mb-1">{name}</div>
                  <div className={`inline-block px-2 py-0.5 rounded-md text-white text-xs font-bold bg-gradient-to-br ${fmsColor(f.fms)}`}>
                    FMS {Math.round(f.fms)}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Index rows */}
          <div className="space-y-1.5">
            {Object.entries(data.index_winners || {}).map(([idxId, w]) => {
              return (
                <div
                  key={idxId}
                  className={`grid gap-3 items-center py-1.5 rounded-lg ${isDark ? "hover:bg-slate-800/50" : "hover:bg-slate-50"}`}
                  style={{ gridTemplateColumns: `120px repeat(${foods.length}, minmax(0,1fr))` }}
                  data-testid={`compare-row-${idxId}`}
                >
                  <div className="text-[11px] font-semibold opacity-80 uppercase pl-1">{idxId}</div>
                  {foods.map((f) => {
                    const val = w.values[f.id];
                    const isWinner = w.winner_food_id === f.id;
                    return (
                      <div
                        key={f.id}
                        className={`text-xs text-center px-2 py-1 rounded-md font-mono ${
                          isWinner
                            ? "bg-emerald-500/15 text-emerald-600 font-bold ring-1 ring-emerald-400"
                            : (isDark ? "text-slate-300" : "text-slate-600")
                        }`}
                      >
                        {val ?? "—"}
                        {isWinner && <span className="ml-1 text-[9px]">★</span>}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>

          <p className="text-[10px] opacity-60 text-center mt-4">{t.disclaimer}</p>
        </div>
      </div>
    </div>
  );
}




// iter469 — Reference modal showing exact DIL/NIS/UIS/COL3/CalDensity formulas
function FormulasModal({ data, lang, isDark, onClose }) {
  const entries = Object.entries(data).filter(([k]) => !["disclaimer_bg", "disclaimer_en"].includes(k));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className={`relative w-full max-w-3xl max-h-[90vh] flex flex-col rounded-2xl shadow-2xl ${isDark ? "bg-slate-900 text-white" : "bg-white text-slate-900"}`}
        onClick={(e) => e.stopPropagation()}
        data-testid="formulas-modal"
      >
        <button onClick={onClose} className="absolute top-3 right-3 p-2 rounded-full hover:bg-slate-500/20" aria-label="Close">
          <X className="w-5 h-5" />
        </button>
        <div className="px-6 pt-6 pb-3 shrink-0 pr-12">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <FlaskConical className="w-5 h-5 text-violet-500" />
            {lang === "bg" ? "Формули за Nutramilo индексите" : "Nutramilo index formulas"}
          </h2>
          <p className={`text-xs mt-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
            {lang === "bg" ? "Прозрачност · peer-reviewed референции" : "Transparency · peer-reviewed references"}
          </p>
        </div>
        <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-4">
          {entries.map(([key, idx]) => (
            <section
              key={key}
              data-testid={`formula-${key}`}
              className={`rounded-xl border p-3 ${isDark ? "bg-slate-800/40 border-slate-700" : "bg-slate-50 border-slate-200"}`}
            >
              <h3 className="text-sm font-bold mb-2 uppercase tracking-wider font-mono text-violet-500">
                {key}
                <span className={`ml-2 normal-case font-sans text-xs ${isDark ? "text-slate-300" : "text-slate-700"}`}>{idx.name_bg}</span>
              </h3>
              <pre className={`whitespace-pre-wrap font-mono text-[11px] p-2 rounded mb-2 overflow-x-auto ${isDark ? "bg-slate-900 text-emerald-300" : "bg-white text-emerald-700 border border-slate-200"}`}>
{idx.formula_text}
              </pre>
              <p className={`text-[11px] leading-relaxed mb-2 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                {idx.description_bg}
              </p>
              {(idx.references || []).length > 0 && (
                <div className="space-y-0.5">
                  {idx.references.map((r, i) => (
                    <a
                      key={i}
                      href={r.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`block text-[10.5px] hover:underline ${isDark ? "text-violet-300" : "text-violet-600"}`}
                    >
                      → {r.label}
                    </a>
                  ))}
                </div>
              )}
            </section>
          ))}
          {data.disclaimer_bg && (
            <p className="text-[10px] text-center opacity-60 pt-2">{data.disclaimer_bg}</p>
          )}
        </div>
      </div>
    </div>
  );
}

